import { Vector3 } from 'three';

/** One of the views the cube offers: a face, an edge or a corner. */
export interface NamedView {
  name: string;
  /** From the part toward the camera. */
  direction: Vector3;
  kind: 'face' | 'edge' | 'corner';
}

export const CUSTOM_VIEW = 'Custom view';

// Z is up; +X is Right and +Y is Back.
function nameFor(x: number, y: number, z: number): string {
  const parts: string[] = [];
  if (y < 0) parts.push('Front');
  if (y > 0) parts.push('Back');
  if (x < 0) parts.push('Left');
  if (x > 0) parts.push('Right');
  if (z > 0) parts.push('Top');
  if (z < 0) parts.push('Bottom');
  return parts.join(' · ');
}

function buildViews(): NamedView[] {
  const views: NamedView[] = [];
  for (const x of [-1, 0, 1]) {
    for (const y of [-1, 0, 1]) {
      for (const z of [-1, 0, 1]) {
        const axes = Math.abs(x) + Math.abs(y) + Math.abs(z);
        if (axes === 0) continue;
        views.push({
          name: nameFor(x, y, z),
          direction: new Vector3(x, y, z).normalize(),
          kind: axes === 1 ? 'face' : axes === 2 ? 'edge' : 'corner',
        });
      }
    }
  }
  return views;
}

export const NAMED_VIEWS: readonly NamedView[] = buildViews();

/** The named view closest to `direction`. */
export function nearestNamedView(direction: Vector3): NamedView {
  const dir = direction.clone().normalize();
  let best = NAMED_VIEWS[0];
  let bestDot = -Infinity;
  for (const view of NAMED_VIEWS) {
    const dot = view.direction.dot(dir);
    if (dot > bestDot) {
      bestDot = dot;
      best = view;
    }
  }
  return best;
}

/** The name of the view along `direction`, or "Custom view" when it is off every named view by more than the tolerance. */
export function viewNameFor(direction: Vector3, toleranceDeg = 2): string {
  const view = nearestNamedView(direction);
  const cos = Math.cos((toleranceDeg * Math.PI) / 180);
  return view.direction.dot(direction.clone().normalize()) >= cos ? view.name : CUSTOM_VIEW;
}

/** A view name as a screen reader should say it. */
export function spokenViewName(name: string): string {
  return name.split(' · ').join(', ');
}
